import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { BaseLivlyApiResponse } from "../../types/Base";
import { BuildingExperienceEnum, OnboardingStatus, User } from "../../types/User";
import auth from "../../utils/auth";
import { BASE_API_URL } from "../../utils/constants";
import { getOnboardingStatuses } from "@/context/ChecklistProvider";
import {
  getStandaloneFutureResidentPath,
  getStandalonePrimaryServicePath,
  isStandaloneFutureResident
} from "@/utils/standaloneRedirect";
import { Spinner } from "@/components/Spinner";
import { MOBILE_KEY, RETURN_TO_URL } from "@/utils/localStorage";
import { WebViewQueryParams } from "@/main";
import { buildWebviewPath } from "@/utils/buildWebviewPath";

const getUserMe = async () => {
  const result = await axios.get<BaseLivlyApiResponse<User>>(
    `${BASE_API_URL}/livly/users/me?v=202404`
  );

  const user = result.data.Data;

  const onboardingStatuses = await getOnboardingStatuses(
    user.userId,
    user.propertyUnitLeaseId
  );

  return { user, onboardingStatuses };
};

export default function CallbackPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [webviewPath, setWebviewPath] = useState<string | null>(null);
  const [error, setError] = useState<boolean | string | null>(null);
  const { data, isFetched } = useQuery(["user", "me"], () => getUserMe(), {
    enabled: !isLoading,
    onError: () => {
      navigate("/no-access");
    }
  });
  const returnToUrl = localStorage.getItem(RETURN_TO_URL);

  const { user, onboardingStatuses } = data || {};

  const handleAuth = useCallback(async () => {
    return await auth.handleAuthentication();
  }, []);

  useEffect(() => {
    const onAuth = async () => {
      try {
        const appState = await handleAuth();

        if (appState) {
          const { accessToken, leaseId, path, userId } = appState;
          localStorage.setItem(MOBILE_KEY, "true");

          if (!accessToken) {
            throw new Error("Access token is required for webview navigation.");
          }

          const route = await buildWebviewPath(
            "",
            userId,
            path || "",
            leaseId,
            accessToken
          );

          setWebviewPath(route);
        }

        setIsLoading(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Error logging in");
      }
    };

    onAuth();
  }, []);

  if (error) {
    return (
      <div>
        <Link to="/logout">Logout</Link>
      </div>
    );
  }

  if (webviewPath) {
    return <Navigate replace to={webviewPath} />;
  }

  if (!isFetched) {
    return (
      <div className="h-screen flex justify-center items-center">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  if (user && onboardingStatuses) {
    if (returnToUrl) {
      localStorage.removeItem(RETURN_TO_URL);

      if (returnToUrl.startsWith("/redirectRouter")) {
        return <Navigate to={returnToUrl} replace />;
      }

      let navigationPathArray = [`/lease/${user.propertyUnitLeaseId}`];

      //handle special cases because urls do not match from old portal to new portal
      if (returnToUrl.includes("maintenance")) {
        const pathOnlyReturnToUrl = returnToUrl.split(/[?#]/)[0];
        const returnToUrlSuffix = returnToUrl.slice(pathOnlyReturnToUrl.length);
        const ticketId = pathOnlyReturnToUrl.split("/").pop();
        navigationPathArray.push(
          `/maintenance/${user.propertyId}/${ticketId}${returnToUrlSuffix}`
        );
      } else {
        navigationPathArray.push(returnToUrl);
      }

      const navigationPath = navigationPathArray.join("");
      return <Navigate to={navigationPath} />;
    }

    const isOnlySelfGuidedProspectLease =
      user.leases.length > 0
        ? user.leases.every(
            (lease) => lease.userType === "Self-Guided Tour Prospect"
          )
        : false;

    if (isOnlySelfGuidedProspectLease) {
      return <Navigate to="/self-guided-takeover" />;
    }

    const path = buildRedirectPath(user, onboardingStatuses);

    return <Navigate to={path} />;
  }

  return (
    <div className="h-screen flex justify-center items-center">
      <Spinner color="livly" size="xl" />
    </div>
  );
}

//http://localhost:5173/sso?mobile=true&platform=droid&path=%2Frent&property_unit_lease_id=5446&app_version=native&session_transfer_token=R6zpNlaMFZ7JwclFTuVyd800D9AqBAeB

export const buildRedirectPath = (
  user: User | null,
  onboardingStatuses: OnboardingStatus[]
) => {
  if (!user) {
    return "/";
  }

  let path = `/lease/${user.propertyUnitLeaseId}/checklist/${user.userId}`;

  if (
    user.presentation?.presentationType === BuildingExperienceEnum.Standalone
  ) {
    path = isStandaloneFutureResident(user)
      ? getStandaloneFutureResidentPath(user)
      : getStandalonePrimaryServicePath(user);
  } else {
    const isOnboardingComplete =
      onboardingStatuses.length > 0 &&
      onboardingStatuses.every(
        (obs) => obs.enabled && (obs.isCompleted || obs.isSkipped)
      );

    if (user.status === "Current" || isOnboardingComplete) {
      path = `/lease/${user.propertyUnitLeaseId}/home`;
    }

    if (user.status === "Past") {
      path = `/lease/${user.propertyUnitLeaseId}/home`;
    }
  }

  return path;
};
